import type { Icon } from "@studio3t/generic-components";
import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from "react";
import { usePersistedState } from "@/hooks/usePersistedState";
import type { TestSuiteCase } from "@/types/api";
import {
  defaultSelection,
  type EditorContents,
  resolveEditorKind,
  type TreePill,
  useEditorContents,
  useTreePreviews,
} from "./editorContents";
// Type-only: pipelineLint.ts imports canStageAs from here at runtime.
import type { EditorErrorRow } from "./editorErrors";
import type { LintStatus } from "./pipelineLint";
import type { SourceVariantsState } from "./schemaVariants";
import type { ScriptRunOutput } from "./scriptRun";
import type { StageProgress } from "./stageProgress";
import { useSchemaDrafts, useScriptDrafts } from "./stagingDrafts";
import {
  type DropEdge,
  isUntitledScriptId,
  moveStagedItem,
  pipelineIsDirty,
  type StagedKind,
  type StagedResource,
  type StagedRole,
  type StagedSchema,
  type StageTabId,
  type StageTabRole,
  type StudioView,
  schemasAfterSourceReplaced,
  schemasAfterStageStaged,
  schemasAfterUnstage,
  type TestAssertionDraft,
  tabDefaultRow,
  untitledName,
  untitledScriptResource,
  withStaged,
  withTransformRenamed,
} from "./stagingModel";
import {
  useRowSelection,
  useSchemaBlockOps,
  useSchemaStaging,
  useSinkDraftOps,
  useTabMemory,
} from "./stagingSelection";
import { useTestStaging } from "./stagingTests";
import {
  type PendingSeed,
  type PendingVariantSwitch,
  useVariantSwitch,
} from "./variantSwitch";

// Shared staging state between the Explorer (drag sources, role badges) and
// the PipelineNavBar (drop targets). The pure staging rules and id schemes
// live in stagingModel.ts; the editor documents in editorContents.ts; the
// per-row drafts in stagingDrafts.tsx; the selection/staging behaviors in
// stagingSelection.tsx; the Tests-tab state machine (suite group, rows,
// assertions, drafts) in stagingTests.tsx. This module owns the staged
// lists, the workbench view state and the provider composing it all — and
// re-exports the pieces so consumers keep a single import surface.

export * from "./editorContents";

import { NO_EXTRAS, type PipelineExtras } from "./pipelineCode";

export * from "./stagingModel";
export type { TestStaging } from "./stagingTests";

interface PipelineStagingValue {
  sources: StagedResource[];
  transforms: StagedResource[];
  sinks: StagedResource[];
  schemas: StagedSchema[];
  tests: StagedResource[];
  stage: (role: StagedRole, resource: StagedResource) => void;
  /** Replaces the four staged lists wholesale — the view-only studio's
   *  rows derived from a deployed pipeline's code (pipelineStagesFromCode);
   *  no adoption or attachment side effects, the sections land as given. */
  loadStagedSections: (sections: {
    sources: StagedResource[];
    transforms: StagedResource[];
    sinks: StagedResource[];
    schemas: StagedSchema[];
  }) => void;
  /** What the pipeline code could NOT be shown as stages for (view-only
   *  studio): invalid JSON, or the entries the parser had to skip. The
   *  Stages panels surface them; empty otherwise. */
  codeIssues: string[];
  setCodeIssues: (issues: string[]) => void;
  /** The parsed pipeline's passthrough fields and encodings
   *  (pipelineCode.PipelineExtras) — every generation feeds them back so a
   *  parse → generate round trip is lossless. NO_EXTRAS until a parse. */
  pipelineExtras: PipelineExtras;
  setPipelineExtras: (extras: PipelineExtras) => void;
  /** Drops the Full pipeline code draft: the generated code is the code
   *  again (the hand-over to staging, see StudioPipelineProvider's
   *  switchToStages). */
  dropPipelineCodeDraft: () => void;
  /** A request to put the code editor on its first lint problem (the
   *  parse error the switch-to-stages modal points at); the token makes
   *  repeats distinct. Consumed by PipelineCodeEditor. */
  codeJumpRequest: { token: number } | null;
  requestCodeJump: () => void;
  /** A request to give the visible editor keyboard focus — the AI
   *  Helper's Insert lands and hands over the caret (TTTL-217). Consumed
   *  by every editor (useEditorFocusRequest); only the one on screen can
   *  take it. */
  editorFocusRequest: { token: number } | null;
  requestEditorFocus: () => void;
  /** Stages a schema explorer leaf into the SCHEMA section, homed on the
   *  given stage tab's unattached block while it has no attachments. */
  stageSchema: (resource: StagedResource, home?: StageTabRole) => void;
  /** Stages a schema and attaches it to the given step (stage-tab drops). */
  stageSchemaFor: (
    resource: StagedResource,
    target: { role: StagedRole; id: string },
  ) => void;
  /** Stages a schema drafted in a tab's unattached block: attaches to that
   *  tab's first schema-less stage when one exists, else stays unattached
   *  (homed on the tab) for a future stage to adopt. */
  stageDraftedSchema: (resource: StagedResource, role?: StageTabRole) => void;
  /** Stages a test-suite explorer row into the TESTS section. */
  stageTest: (resource: StagedResource) => void;
  /** The introspected source's document-shape variants behind the input
   *  editor's variant bar (TTTL-179 gap 4); null with nothing loaded. */
  sourceVariants: SourceVariantsState | null;
  /** The bar's pick: applies the switch, or parks it behind the confirm
   *  modal when the input document / seeded schema draft was edited. */
  requestVariantSwitch: (index: number) => void;
  pendingVariantSwitch: PendingVariantSwitch | null;
  confirmVariantSwitch: () => void;
  cancelVariantSwitch: () => void;
  /** A drop-time seed waiting on the replace-schema modal (the slot held
   *  a custom draft); null otherwise. */
  pendingSeed: PendingSeed | null;
  /** The staged source/sink ids whose drop-time schema seed is still
   *  resolving — their SCHEMA slots read "in progress" (schemaSeeding). */
  seedingStageIds: readonly string[];
  confirmSeedReplace: () => void;
  cancelSeedReplace: () => void;
  /** True while the active source's slot holds a custom schema — the
   *  footer trigger reads "Custom variant" with nothing selected. */
  variantIsCustom: boolean;
  /** ⌘S: whoever owns the SELECTED row's save flow registers its handler
   *  here (null on unmount/deselect); requestActiveSave runs it, falling
   *  back to the toolbar's guarded draft save. */
  setActiveSaveHandler: (handler: (() => void) | null) => void;
  setDraftSaveHandler: (handler: (() => void) | null) => void;
  requestActiveSave: () => void;
  /** The toolbar's guarded draft save, directly (the palette's "Save as
   *  draft") — row-level edits notwithstanding. */
  requestDraftSave: () => void;
  /** The Tests editor's current assertion lines, or null while it shows
   *  its links face. Lines written with no test on the editor start the
   *  untitled draft. */
  testDraft: TestAssertionDraft[] | null;
  setTestDraft: (draft: TestAssertionDraft[]) => void;
  /** The test the editor holds: a staged test id, an untitled-draft id,
   *  TESTS_EDITOR_ID when the suite group / pseudo-row took the hand-off
   *  after a close/delete/discard (full links face, highlighted row), or
   *  null — NOTHING selected: no row highlight, the links face down to
   *  the bare "Add a new test suite" link. */
  testEditorId: string | null;
  /** True while the editor panel shows the selected test READ-ONLY (the
   *  viewing face a drop or a plain row click opens — Figma 14785-268156)
   *  instead of the assertion editor. */
  testViewOnly: boolean;
  /** True while a row's working copy diverges from its saved lines —
   *  drafts survive switching rows, so any row can be dirty. */
  isTestDirty: (id: string) => boolean;
  /** A test's strict-mode flag as the editor shows it: the stored case's,
   *  or the unsaved toggle (which makes the row dirty until Save). The
   *  engine fails a strict case on any output field no assertion covers. */
  strictModeOf: (id: string) => boolean;
  setStrictMode: (id: string, value: boolean) => void;
  /** The saved assertion lines per staged test. */
  testAssertions: Record<string, TestAssertionDraft[]>;
  /** The unsaved working copies per row (untitled drafts included). */
  testDrafts: Record<string, TestAssertionDraft[]>;
  /** Loads a row into the editor — a kept working copy wins over the
   *  saved lines; also reopens kept untitled drafts. */
  openTestInEditor: (id: string) => void;
  /** Shows a staged test's saved assertions read-only (the viewing face —
   *  no working copy is minted, so viewing never dirties a row). */
  openTestViewer: (id: string) => void;
  /** Mints a NEW untitled draft; the kept ones stay as their own rows. */
  openNewTestDraft: () => void;
  /** The just-minted draft whose row opens with its name input active —
   *  the row consumes (clears) it on mount. */
  freshTestDraftId: string | null;
  clearFreshTestDraft: () => void;
  /** Same for a just-minted script draft — its row opens the name input
   *  WITHOUT taking focus (the mint happens mid-typing in the editor). */
  freshScriptDraftId: string | null;
  clearFreshScriptDraft: () => void;
  /** Deselect/close: the pseudo-row takes the hand-off; drafts stay. */
  closeTestEditor: () => void;
  /** Drops an untitled row's draft (its minus). */
  discardUntitledTest: (untitledId: string) => void;
  /** Names an untitled draft into a staged test row (client-side); the
   *  caller provides the row icon. */
  saveNewTest: (untitledId: string, name: string, icon: Icon) => void;
  /** Persists a row's working copy onto its saved lines. */
  saveTestEdits: (id: string) => void;
  /** Returns a dirty row to its saved lines (the revert row action —
   *  Figma 14785-272031): the working copy's changes are dropped. The
   *  open editor keeps the row on screen with the saved lines back. */
  discardTestEdits: (id: string) => void;
  /** Renames a staged test (client-side; the id stays). */
  renameTest: (id: string, name: string) => void;
  /** The staged suite group, or null while the panel is empty. The panel
   *  stages ONE suite (interim, backend stores one test_suite_id); its
   *  cases are the `tests` rows, untitled drafts appended after. suiteId
   *  null = PENDING: named locally but not yet in the catalog — the
   *  stored suite lands with its first non-empty test. */
  stagedSuite: { suiteId: string | null; name: string } | null;
  /** REPLACES the staged group with this suite's cases; drafts and the
   *  selection are dropped (callers confirm-gate on hasDirtyTestWork). */
  stageSuiteGroup: (
    suite: { id: string; name: string; cases: TestSuiteCase[] },
    icon: Icon,
  ) => void;
  /** REPLACES the staged group with a PENDING suite: named, empty, and
   *  NOT in the catalog — no tree row until a first non-empty test is
   *  saved (useEnsureStagedSuite creates the stored suite then). */
  stagePendingSuite: (name: string) => void;
  /** Points the pending group at its just-created catalog row. */
  attachSuiteId: (suiteId: string) => void;
  /** Clears the group (catalog untouched); the pseudo-row takes the
   *  hand-off. */
  unstageSuiteGroup: () => void;
  /** Appends already-persisted cases as saved rows after the staged ones
   *  (test generation's landing); drafts and the selection stay. */
  appendSavedTests: (
    entries: Array<{
      name: string;
      rows: TestAssertionDraft[];
      strictMode?: boolean;
    }>,
    icon: Icon,
  ) => string[];
  /** Follows a catalog suite rename on the staged header row. */
  renameStagedSuite: (name: string) => void;
  /** True when the group holds an untitled draft or any dirty row — the
   *  replace/unstage confirm gate. */
  hasDirtyTestWork: () => boolean;
  /** Removes a resource from whichever section holds it. */
  unstage: (id: string) => void;
  /** Detaches a schema from one stage; the last reference unstages it. */
  detachSchema: (
    schemaId: string,
    target: { role: StagedRole; id: string },
  ) => void;
  /** Removes a schema from a tab's bottom block; a schema referenced
   *  nowhere else leaves staging entirely. */
  /** Clears a schema's waiting state on ONE tab's bottom block; a schema
   *  referenced nowhere else leaves staging. */
  removePending: (schemaId: string, tab: StageTabRole) => void;
  /** Renames a staged schema in every block that shows it (the backend
   *  rename is the caller's job — this updates the staged rows). */
  renameSchema: (schemaId: string, name: string) => void;
  /** Renames a staged transform (the backend rename is the caller's job).
   *  A script's filename is its resource id, so the staged id changes:
   *  schema attachments, drafts and the selection follow it. */
  renameTransform: (
    oldId: string,
    next: { id: string; name: string; provider?: StagedResource["provider"] },
  ) => void;
  /** Renames a staged transform row client-side only (an untitled
   *  draft's local rename — the file is born at Save, so there is
   *  nothing stored to move; pass the same id to keep it). */
  renameTransformRow: (
    oldId: string,
    next: { id: string; name: string; provider?: StagedResource["provider"] },
  ) => void;
  /** Per-row script drafts: staged transform rows while their text diverges
   *  from the stored script, plus one entry per "Untitled script*" row
   *  (untitled ids; any number can be drafted side by side). */
  scriptDrafts: Record<string, string>;
  /** Drops an untitled row's draft (its minus); when it was on the editor,
   *  the pseudo-row takes the hand-off. */
  discardScriptDraft: (id: string) => void;
  /** Drops a row's script draft without touching the editor (post-save). */
  clearScriptDraft: (rowId: string) => void;
  /** Per-row stored text as the Studio last saved it (TTTL-224): what a
   *  run and the AI Helper read for a draft-free row instead of fetching
   *  the file back. A row not here was staged without being saved. */
  savedScripts: Record<string, string>;
  /** Records a row's stored text after a save. */
  rememberSavedScript: (rowId: string, text: string) => void;
  /** Rebaselines the script dirty tracking after a row save. */
  markScriptSaved: () => void;
  /** Reorders the TRANSFORM section by dropping one row onto another. */
  reorderTransforms: (
    sourceId: string,
    targetId: string,
    edge: DropEdge,
  ) => void;
  /** The latest script-row run, shown in the Output panel. */
  scriptRunOutput: ScriptRunOutput | null;
  /** Publishes a run result and opens the Output panel at half height. */
  showScriptRunOutput: (output: ScriptRunOutput) => void;
  /** Output panel collapse state (the bar at the workbench bottom). */
  outputCollapsed: boolean;
  setOutputCollapsed: (collapsed: boolean) => void;
  /** Bumped per showScriptRunOutput — StudioPanels resizes the Output
   *  panel to half the workbench on each bump. */
  outputHalfRequest: number;
  /** The latest-started run's row id (or PIPELINE_RUN_ID) — each Run
   *  button disables only while this is its OWN id. */
  scriptRunningId: string | null;
  setScriptRunningId: React.Dispatch<React.SetStateAction<string | null>>;
  /** Staged kind for an explorer row; sources win when an id is in both. */
  roleOf: (id: string) => StagedKind | null;
  /** JSON document driving the pipeline input — edited in the SOURCE editor
   *  panel; "" means the editor shows its empty state. */
  inputDocument: string;
  setInputDocument: (value: string) => void;
  /** The introspected sample the input document was loaded from — the
   *  "input edited" baseline (stagingModel.inputEdited); null while
   *  nothing was loaded. */
  inputOrigin: string | null;
  /** True while a selection's content fetch (a staged row's stored
   *  script/schema, a source's sample) is in flight — writes behind a
   *  selection wait on it, or the fetch lands after them. */
  contentLoading: boolean;
  /** The input stored in the open pipeline — "" for scratch. Set by
   *  StudioPipelineProvider on open/save/deploy; the "input edited" badge
   *  baseline (inputEditedSinceOpen) and what a source reselect restores
   *  instead of regenerating a sample (inputFillOnSelect, TTTL-191). */
  savedInput: string;
  setSavedInput: (value: string) => void;
  /** Rhai transform script edited in the TRANSFORM editor panel; "" means
   *  the script editor shows its templates zero state. */
  scriptDocument: string;
  setScriptDocument: (value: string) => void;
  /** JSON schema edited in the sink-tab SCHEMA editor panel; "" means the
   *  schema editor shows its empty state. */
  schemaDocument: string;
  setSchemaDocument: (value: string) => void;
  /** The selected staged schema's definition as loaded/last saved — the
   *  dirty baseline for its row; null while the editor holds an unsaved
   *  draft (pseudo-row flow). */
  schemaOrigin: string | null;
  /** Unsaved schema drafts per SCHEMA slot, keyed by the slot's editor row
   *  id — kept staged in their block until saved or discarded. */
  schemaDrafts: Record<string, string>;
  /** Drops a slot's unsaved draft (the untitled row's minus). */
  discardSchemaDraft: (rowId: string) => void;
  /** Drops a remembered draft without touching the editor — for a row
   *  whose edits were just saved (the editor content IS the saved text). */
  clearSchemaDraft: (rowId: string) => void;
  /** Display names for the slot drafts' "Untitled schema*" rows — local
   *  until the Save modal names the stored schema; missing = default. */
  schemaDraftNames: Record<string, string>;
  setSchemaDraftName: (rowId: string, name: string) => void;
  /** The just-minted slot draft whose row opens its name input hands-off
   *  (the mint keystroke lands in the schema editor); the row consumes
   *  (clears) it on mount. */
  freshSchemaDraftRowId: string | null;
  clearFreshSchemaDraft: () => void;
  /** Marks the current schema editor content as saved (origin = content). */
  markSchemaSaved: () => void;
  /** Explorer resource whose content the editor currently shows (active
   *  pill in the tree), or null. */
  editorSourceId: string | null;
  /** Introspects a clicked explorer resource and shows its sample document
   *  in the input editor (no-op for udf/http providers). */
  showSourceSample: (resource: StagedResource) => void;
  /** Loads a clicked udf script leaf into the transform script editor. */
  showScript: (resource: StagedResource) => void;
  /** Previews a clicked schema tree leaf in the schema editor (staged rows
   *  deselect but stay staged). */
  showSchema: (resource: StagedResource) => void;
  /** Drops the tree's active pill — used when a nav-bar row takes the
   *  selection so only one item is ever marked across both panels. */
  clearEditorSource: () => void;
  /** Which editor the Studio panel shows for the current selection. */
  editorKind: "input" | "script" | "schema";
  /** Nav-bar selection, resolved with the SOURCE default fallback. */
  effectiveSelectedId: string | null;
  /** Toggles a nav-bar row selection (entering an editor pseudo-row anew
   *  resets that editor to its zero state). */
  toggleSelected: (id: string) => void;
  /** Clears a stale nav-bar selection (e.g. after replacing the source). */
  deselectIf: (id: string) => void;
  /** Toolbar workbench view: Stages, or the combined Full pipeline view
   *  (code editor over the graph strip — Figma 14419-264437). */
  studioView: StudioView;
  setStudioView: (view: StudioView) => void;
  /** Active stage tab of the Stages view (Source/Transform/Sink/Tests). */
  stageTab: StageTabId;
  setStageTab: (tab: StageTabId) => void;
  /** User edits of the Full pipeline code view (PipelineCodeEditor). The
   *  draft type lives in pipelineCode.ts; staging changes invalidate it. */
  pipelineCodeDraft: { text: string; basedOn: string } | null;
  setPipelineCodeDraft: (draft: { text: string; basedOn: string }) => void;
  /** Settle state of the pipeline-code lint pass, reported by
   *  PipelineCodeEditor — the toolbar gates Run on it in the code view. */
  pipelineCodeLintStatus: LintStatus;
  setPipelineCodeLintStatus: (status: LintStatus) => void;
  /** Doc text the last settled lint pass was computed against — a code
   *  editor remounting on that exact text keeps its verdict instead of
   *  restarting at pending (the Unresolved chip must not blink on a
   *  Stages/Full round trip). A ref: it has no renders to drive. */
  pipelineCodeLintedText: { current: string | null };
  /** The error rows of that pass (the status bar's count and the header's
   *  Unresolved list); empty until a pass reports. */
  pipelineCodeErrors: EditorErrorRow[];
  setPipelineCodeErrors: (rows: EditorErrorRow[]) => void;
  /** The toolbar's stage progress as the last settled lint pass read the
   *  code (PipelineCodeEditor); null until a pass reports. */
  pipelineCodeProgress: StageProgress | null;
  setPipelineCodeProgress: (progress: StageProgress | null) => void;
  /** Graph node id of the stage the code cursor is inside (source /
   *  mw-<i> / sink-<i>), reported by PipelineCodeEditor — the graph strip
   *  zooms in on that card. */
  focusedStageId: string | null;
  setFocusedStageId: (id: string | null) => void;
  /** Set when the graph should restart from the whole-pipeline overview
   *  (paste): carries the pasted text so the graph can jump straight to
   *  it — waiting for the draft state would race React's batching. */
  graphReset: { token: number; text: string } | null;
  resetGraphView: (text: string) => void;
  /** Whether "Clear and start fresh" has anything to clear. */
  pipelineDirty: boolean;
  /** Anything in the studio that a leave would lose (hasUnsavedStudioWork). */
  hasUnsavedWork: boolean;
  /** Back to the pristine studio: unstages everything, drops the code
   *  draft and editor contents, restores the default selection and
   *  restarts the graph on the template. */
  clearPipeline: () => void;
}

const PipelineStagingContext = createContext<PipelineStagingValue | null>(null);

const withoutId = (id: string) => (prev: StagedResource[]) =>
  prev.some((r) => r.id === id) ? prev.filter((r) => r.id !== id) : prev;

// A freshly minted untitled script draft appends its staged-row face —
// drafts keep their creation order among dropped scripts and reorder with
// the same drag handles. Numbered by mint order among the open untitled
// rows. Own hook: the just-minted flag its row's name input consumes
// (without taking focus — the mint happens mid-typing in the code editor)
// is local to this concern alone.
function useUntitledScriptDrafts(
  setTransforms: React.Dispatch<React.SetStateAction<StagedResource[]>>,
) {
  const [freshScriptDraftId, setFreshScriptDraftId] = useState<string | null>(
    null,
  );
  const clearFreshScriptDraft = useCallback(
    () => setFreshScriptDraftId(null),
    [],
  );

  const addUntitledScriptRow = useCallback(
    (id: string) => {
      setTransforms((prev) => [
        ...prev,
        untitledScriptResource(
          id,
          untitledName(
            "Untitled script",
            prev.filter((t) => isUntitledScriptId(t.id)).length,
          ),
        ),
      ]);
      setFreshScriptDraftId(id);
    },
    [setTransforms],
  );

  return useMemo(
    () => ({ addUntitledScriptRow, freshScriptDraftId, clearFreshScriptDraft }),
    [addUntitledScriptRow, freshScriptDraftId, clearFreshScriptDraft],
  );
}

/** A transform row's new identity. A script rename changes the staged id
 *  (the filename IS the id), so the name — and the provider on a move
 *  between services — ride along with it. */
type TransformRename = {
  id: string;
  name: string;
  provider?: StagedResource["provider"];
};

// Schema attachments pointing at a renamed script follow its new id.
// withTransformRenamed works off the transform rows, so the schema pass is
// fed a stand-in row carrying the old id.
function schemasAfterTransformRenamed(
  prev: StagedSchema[],
  oldId: string,
  next: TransformRename,
): StagedSchema[] {
  return withTransformRenamed(
    [{ id: oldId, name: oldId } as StagedResource],
    prev,
    oldId,
    next,
  ).schemas;
}

// The four staged section lists (source/transform/sink/schema) and the
// operations that only move rows between them: the wholesale load of a
// deployed pipeline's sections, the cross-section removal of one id, and
// the full reset. The setters come out with the lists — the staging
// behaviors (useSectionOps) and the cross-list ones that fold in test
// state (useStagedLists) drive them.
function useSectionState() {
  const [sources, setSources] = useState<StagedResource[]>([]);
  const [transforms, setTransforms] = useState<StagedResource[]>([]);
  const [sinks, setSinks] = useState<StagedResource[]>([]);
  const [schemas, setSchemas] = useState<StagedSchema[]>([]);

  const loadStagedSections = useCallback(
    (sections: {
      sources: StagedResource[];
      transforms: StagedResource[];
      sinks: StagedResource[];
      schemas: StagedSchema[];
    }) => {
      setSources(sections.sources);
      setTransforms(sections.transforms);
      setSinks(sections.sinks);
      setSchemas(sections.schemas);
    },
    [],
  );

  const removeFromSections = useCallback((id: string) => {
    const remove = withoutId(id);
    setSources(remove);
    setTransforms(remove);
    setSinks(remove);
    setSchemas((prev) => schemasAfterUnstage(prev, id));
  }, []);

  const clearSections = useCallback(() => {
    setSources([]);
    setTransforms([]);
    setSinks([]);
    setSchemas([]);
  }, []);

  return useMemo(
    () => ({
      sources,
      transforms,
      sinks,
      schemas,
      setSources,
      setTransforms,
      setSinks,
      setSchemas,
      loadStagedSections,
      removeFromSections,
      clearSections,
    }),
    [
      sources,
      transforms,
      sinks,
      schemas,
      loadStagedSections,
      removeFromSections,
      clearSections,
    ],
  );
}

/** The section lists with their setters — what the staging behaviors run on. */
type SectionState = ReturnType<typeof useSectionState>;

// What a drop or a row action does to the section lists: staging a resource
// into its section, reordering the TRANSFORM rows, following a script
// rename, and minting untitled script rows.
function useSectionOps(sections: SectionState) {
  const { sources, setSources, setTransforms, setSinks, setSchemas } = sections;

  const stage = useCallback(
    (role: StagedRole, resource: StagedResource) => {
      const setterFor = {
        source: setSources,
        transform: setTransforms,
        sink: setSinks,
      } as const;
      setterFor[role]((prev) => withStaged(prev, role, resource));
      // SOURCE is a single slot: a replaced source's schema detaches into
      // the tab's bottom block first, so the adoption pass below hands it
      // to the replacement.
      const replacedIds =
        role === "source"
          ? sources.filter((r) => r.id !== resource.id).map((r) => r.id)
          : [];
      // A waiting unattached schema (homed on the same tab) joins the new
      // stage's group.
      setSchemas((prev) =>
        schemasAfterStageStaged(
          schemasAfterSourceReplaced(prev, replacedIds),
          role,
          resource.id,
        ),
      );
    },
    [sources, setSources, setTransforms, setSinks, setSchemas],
  );

  // Named apart from the per-row draft TEXTS two other `scriptDrafts`
  // carry (the context key and the provider-level useScriptDrafts group) —
  // this one only mints untitled rows.
  const scriptDraftMinting = useUntitledScriptDrafts(setTransforms);

  const reorderTransforms = useCallback(
    (sourceId: string, targetId: string, edge: DropEdge) => {
      setTransforms((prev) => moveStagedItem(prev, sourceId, targetId, edge));
    },
    [setTransforms],
  );

  // A script rename changes the staged id (the filename is the id) — the
  // transform row and any schema attachments pointing at it follow.
  const renameTransformRow = useCallback(
    (oldId: string, next: TransformRename) => {
      setTransforms(
        (prev) => withTransformRenamed(prev, [], oldId, next).transforms,
      );
      setSchemas((prev) => schemasAfterTransformRenamed(prev, oldId, next));
    },
    [setTransforms, setSchemas],
  );

  return useMemo(
    () => ({
      ...scriptDraftMinting,
      stage,
      reorderTransforms,
      renameTransformRow,
    }),
    [scriptDraftMinting, stage, reorderTransforms, renameTransformRow],
  );
}

// The staged sections and their operations, composing in the Tests tab's
// whole state machine from stagingTests.tsx. Two operations here fold test
// state into the matching cross-list behavior: removeEverywhere hands an
// unstaged id to removeTestEverywhere (an unstaged test takes its saved
// assertions and working copy along, same as any other section), and
// clearAll hands off to clearTests. stageTest, needed only by the
// explorer's test-suite drop (mirroring stage's source/transform/sink
// drop), passes straight through.
function useStagedLists() {
  const sections = useSectionState();
  const ops = useSectionOps(sections);
  const testStaging = useTestStaging();
  const { sources, transforms, sinks, schemas } = sections;

  const roleOf = useCallback(
    (id: string): StagedKind | null => {
      const kinds: Array<[StagedKind, ReadonlyArray<{ id: string }>]> = [
        ["source", sources],
        ["transform", transforms],
        ["sink", sinks],
        ["schema", schemas],
        ["test", testStaging.tests],
      ];
      const hit = kinds.find(([, list]) => list.some((r) => r.id === id));
      return hit ? hit[0] : null;
    },
    [sources, transforms, sinks, schemas, testStaging.tests],
  );

  const removeEverywhere = useCallback(
    (id: string) => {
      sections.removeFromSections(id);
      testStaging.removeTestEverywhere(id);
    },
    [sections.removeFromSections, testStaging.removeTestEverywhere],
  );

  const clearAll = useCallback(() => {
    sections.clearSections();
    testStaging.clearTests();
  }, [sections.clearSections, testStaging.clearTests]);

  // testStaging and ops spread whole — their extra internals
  // (removeTestEverywhere, clearTests, used only above to fold test state
  // into removeEverywhere/clearAll) are harmless surplus on the returned
  // object, same deal as the provider-level spreads below.
  return useMemo(
    () => ({
      sources,
      transforms,
      sinks,
      schemas,
      ...testStaging,
      ...ops,
      setSchemas: sections.setSchemas,
      roleOf,
      removeEverywhere,
      clearAll,
      loadStagedSections: sections.loadStagedSections,
    }),
    [
      sources,
      transforms,
      sinks,
      schemas,
      testStaging,
      ops,
      sections.setSchemas,
      sections.loadStagedSections,
      roleOf,
      removeEverywhere,
      clearAll,
    ],
  );
}

/** The staged lists hook group — the shape the behavior hooks compose on. */
export type StagedLists = ReturnType<typeof useStagedLists>;

// Workbench-level view state: the toolbar view switch, the stage tabs, the
// Full-pipeline code draft with its lint status, and the graph signals.
function useWorkbenchView(initialView: StudioView) {
  // Toolbar view switch (Figma Bridge_Pipeline/toolbar).
  const [studioView, setStudioView] = useState<StudioView>(initialView);
  // Why the pipeline code could not (fully) be shown as stages — set by the
  // view-only load, cleared with the rest of the view.
  const [codeIssues, setCodeIssues] = useState<string[]>([]);
  // The parsed pipeline's passthrough extras (name, checkpoint, encodings).
  const [pipelineExtras, setPipelineExtras] =
    useState<PipelineExtras>(NO_EXTRAS);
  const dropPipelineCodeDraft = useCallback(
    () => setPipelineCodeDraft(null),
    [],
  );
  const [codeJumpRequest, setCodeJumpRequest] = useState<{
    token: number;
  } | null>(null);
  const requestCodeJump = useCallback(
    () => setCodeJumpRequest((prev) => ({ token: (prev?.token ?? 0) + 1 })),
    [],
  );
  const [editorFocusRequest, setEditorFocusRequest] = useState<{
    token: number;
  } | null>(null);
  const requestEditorFocus = useCallback(
    () => setEditorFocusRequest((prev) => ({ token: (prev?.token ?? 0) + 1 })),
    [],
  );
  // Stage tab of the Stages view — survives view switches.
  const [stageTab, setStageTab] = useState<StageTabId>("source");
  // Edits of the Full pipeline code view, pinned to their generated base.
  const [pipelineCodeDraft, setPipelineCodeDraft] = useState<{
    text: string;
    basedOn: string;
  } | null>(null);
  // Lint settle state of the code view; "pending" until a pass reports.
  const [pipelineCodeLintStatus, setPipelineCodeLintStatus] =
    useState<LintStatus>("pending");
  // Text of the last settled pass (see the context field's comment).
  const pipelineCodeLintedText = useRef<string | null>(null);
  const [pipelineCodeErrors, setPipelineCodeErrors] = useState<
    EditorErrorRow[]
  >([]);
  const [pipelineCodeProgress, setPipelineCodeProgress] =
    useState<StageProgress | null>(null);
  // Stage card under the code cursor (Full pipeline view).
  const [focusedStageId, setFocusedStageId] = useState<string | null>(null);
  // Graph restart signal (paste replaces the doc → show the whole graph).
  const [graphReset, setGraphReset] = useState<{
    token: number;
    text: string;
  } | null>(null);

  const resetGraphView = useCallback(
    (text: string) =>
      setGraphReset((prev) => ({ token: (prev?.token ?? 0) + 1, text })),
    [],
  );

  // Fresh pipeline code is empty — restart the graph on it, whole-graph
  // fit — and land on the Stages view's Source tab, wherever the cleared
  // pipeline had the studio (an opened job shows in the Full pipeline
  // view): a fresh start always begins at the source.
  const clearView = useCallback(() => {
    setPipelineCodeDraft(null);
    pipelineCodeLintedText.current = null;
    setFocusedStageId(null);
    resetGraphView("");
    setStudioView("stages");
    setStageTab("source");
    setCodeIssues([]);
    setPipelineExtras(NO_EXTRAS);
  }, [resetGraphView]);

  return useMemo(
    () => ({
      studioView,
      setStudioView,
      stageTab,
      setStageTab,
      pipelineCodeDraft,
      setPipelineCodeDraft,
      pipelineCodeLintStatus,
      setPipelineCodeLintStatus,
      pipelineCodeLintedText,
      pipelineCodeErrors,
      setPipelineCodeErrors,
      pipelineCodeProgress,
      setPipelineCodeProgress,
      focusedStageId,
      setFocusedStageId,
      graphReset,
      resetGraphView,
      clearView,
      codeIssues,
      setCodeIssues,
      pipelineExtras,
      setPipelineExtras,
      dropPipelineCodeDraft,
      codeJumpRequest,
      requestCodeJump,
      editorFocusRequest,
      requestEditorFocus,
    }),
    [
      studioView,
      stageTab,
      pipelineCodeDraft,
      pipelineCodeLintStatus,
      pipelineCodeErrors,
      pipelineCodeProgress,
      focusedStageId,
      graphReset,
      resetGraphView,
      clearView,
      codeIssues,
      pipelineExtras,
      dropPipelineCodeDraft,
      codeJumpRequest,
      requestCodeJump,
      editorFocusRequest,
      requestEditorFocus,
    ],
  );
}

// The latest script-row run for the Output panel. Publishing a result
// into a COLLAPSED panel opens it at half the workbench (the
// outputHalfRequest bump signals StudioPanels to resize); an already
// open panel keeps whatever size the user gave it.
const OUTPUT_COLLAPSED_STORAGE_KEY = "studio:output-collapsed";

function serializeOutputCollapsed(collapsed: boolean): string | null {
  return collapsed ? null : "false";
}

function useOutputPanel() {
  const [scriptRunOutput, setScriptRunOutput] =
    useState<ScriptRunOutput | null>(null);
  // Open or collapsed is remembered per user (TTTL-186); collapsed is
  // the default and is stored as absence.
  const [outputCollapsed, setOutputCollapsed] = usePersistedState<boolean>(
    OUTPUT_COLLAPSED_STORAGE_KEY,
    (raw) => raw !== "false",
    serializeOutputCollapsed,
  );
  const [outputHalfRequest, setOutputHalfRequest] = useState(0);
  const [scriptRunningId, setScriptRunningId] = useState<string | null>(null);
  const showScriptRunOutput = useCallback(
    (output: ScriptRunOutput) => {
      setScriptRunOutput(output);
      if (!outputCollapsed) return;
      setOutputCollapsed(false);
      setOutputHalfRequest((n) => n + 1);
    },
    [outputCollapsed, setOutputCollapsed],
  );
  return useMemo(
    () => ({
      scriptRunOutput,
      showScriptRunOutput,
      outputCollapsed,
      setOutputCollapsed,
      outputHalfRequest,
      scriptRunningId,
      setScriptRunningId,
    }),
    [
      scriptRunOutput,
      showScriptRunOutput,
      outputCollapsed,
      outputHalfRequest,
      scriptRunningId,
      setOutputCollapsed,
    ],
  );
}

// Everything the studio holds lives only in memory until a workspace
// save — a refresh or tab close silently drops it. Anything worth keeping:
// staged sections / a diverged code draft (pipelineDirty), unsaved SCHEMA
// slot drafts, or editor content (input/script/schema). Editor content also
// covers plain previews — a small over-warn accepted so a typed-but-
// unstaged transform never slips through (there's no input/script
// dirty-vs-preview tracking yet).
function hasUnsavedStudioWork(options: {
  pipelineDirty: boolean;
  editor: EditorContents;
  schemaDrafts: Record<string, string>;
}): boolean {
  return (
    options.pipelineDirty ||
    options.editor.inputDocument !== "" ||
    options.editor.scriptDocument !== "" ||
    options.editor.schemaDocument !== "" ||
    Object.values(options.schemaDrafts).some((text) => text !== "")
  );
}

export function PipelineStagingProvider({
  children,
  // The view the studio paints first. A pipeline arriving by ?workspace
  // opens on its code (StudioPipelineProvider.openDraft), so the page
  // seeds that view and the empty Stages never get a frame — the switch
  // in the open effect would come one paint too late.
  initialView = "stages",
}: {
  children: ReactNode;
  initialView?: StudioView;
}) {
  const lists = useStagedLists();
  const editor = useEditorContents();
  const view = useWorkbenchView(initialView);
  const output = useOutputPanel();
  // Explorer leaf whose content the editor is showing (active pill in the
  // tree) and which editor it belongs in — like /studio's explorerActiveIds.
  const [editorSource, setEditorSource] = useState<TreePill>(null);
  // Nav-bar selection (one across all sections); the tree pill and this are
  // mutually exclusive.
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const effectiveSelectedId = defaultSelection(
    selectedId,
    editorSource !== null,
    lists.sources,
  );

  const drafts = useSchemaDrafts(editor, effectiveSelectedId);
  const isTransformRow = useCallback(
    (id: string) => lists.transforms.some((t) => t.id === id),
    [lists.transforms],
  );
  const scriptDrafts = useScriptDrafts(
    editor,
    effectiveSelectedId,
    isTransformRow,
    setSelectedId,
    setEditorSource,
    lists.addUntitledScriptRow,
  );

  const previews = useTreePreviews(editor, setEditorSource, setSelectedId);
  const schemaStaging = useSchemaStaging({
    lists,
    loadSchema: editor.loadSchema,
    setEditorSource,
    setSelectedId,
    clearDraftFor: drafts.clearDraftFor,
  });
  const selection = useRowSelection({
    lists,
    editor,
    effectiveSelectedId,
    drafts,
    scriptDrafts,
    setEditorSource,
    setSelectedId,
  });
  const variantSwitch = useVariantSwitch({
    editor,
    drafts,
    schemas: lists.schemas,
    sources: lists.sources,
    sinks: lists.sinks,
    attachSchema: schemaStaging.attachSchema,
    stageSchemaFor: schemaStaging.stageSchemaFor,
  });
  const sinkOps = useSinkDraftOps({
    lists,
    editor,
    drafts,
    scriptDrafts,
    effectiveSelectedId,
    selectedId,
    setSelectedId,
    setEditorSource,
    seedSchemaDraft: variantSwitch.seedStageSchemaDraft,
  });
  const blockOps = useSchemaBlockOps({
    lists,
    editor,
    drafts,
    unstage: sinkOps.unstage,
    selectedId,
    setSelectedId,
  });
  // The entered tab's default row (nothing remembered to restore): stage
  // tabs select their first staged row — or empty-tab editor pseudo-row —
  // like a click (tabDefaultRow); the Tests tab opens its first staged
  // test's viewer, but only over NOTHING — a pseudo-row hand-off
  // (TESTS_EDITOR_ID) is a remembered state and stays.
  const selectTabDefault = useCallback(
    (tab: StageTabId) => {
      if (tab === "tests") {
        if (lists.testEditorId === null && lists.tests.length > 0)
          lists.openTestViewer(lists.tests[0].id);
        return;
      }
      selection.toggleSelected(tabDefaultRow(tab, lists));
    },
    [lists, selection],
  );
  const selectStageTab = useTabMemory({
    stageTab: view.stageTab,
    setStageTab: view.setStageTab,
    selectedId,
    editorSource,
    setEditorSource,
    setSelectedId,
    roleOf: lists.roleOf,
    restoreSelection: selection.restoreSelection,
    selectTabDefault,
  });

  const clearEditorSource = useCallback(() => setEditorSource(null), []);

  // ⌘S's dispatch: the component owning the selected row's save flow
  // registers here (registration is synchronous, so there is no effect
  // ordering to race); with no row-level edit active the toolbar's
  // guarded draft save takes the keystroke.
  const activeSaveRef = useRef<(() => void) | null>(null);
  const draftSaveRef = useRef<(() => void) | null>(null);
  const setActiveSaveHandler = useCallback((handler: (() => void) | null) => {
    activeSaveRef.current = handler;
  }, []);
  const setDraftSaveHandler = useCallback((handler: (() => void) | null) => {
    draftSaveRef.current = handler;
  }, []);
  const requestActiveSave = useCallback(() => {
    (activeSaveRef.current ?? draftSaveRef.current)?.();
  }, []);
  const requestDraftSave = useCallback(() => {
    draftSaveRef.current?.();
  }, []);

  // "Clear and start fresh": back to the pristine studio in one shot.
  const clearPipeline = useCallback(() => {
    lists.clearAll();
    editor.clearEditor("input");
    editor.clearEditor("script");
    editor.clearEditor("schema");
    editor.setSavedInput("");
    drafts.clearAllDrafts();
    scriptDrafts.clearAllScriptDrafts();
    setEditorSource(null);
    setSelectedId(null);
    view.clearView();
  }, [
    lists.clearAll,
    editor.clearEditor,
    editor.setSavedInput,
    drafts.clearAllDrafts,
    scriptDrafts.clearAllScriptDrafts,
    view.clearView,
  ]);

  const editorKind = resolveEditorKind(
    effectiveSelectedId,
    editorSource?.kind ?? null,
    lists.transforms,
    lists.schemas,
  );

  const pipelineDirty = pipelineIsDirty({
    ...lists,
    draft: view.pipelineCodeDraft,
    inputDocument: editor.inputDocument,
    schemaDocument: editor.schemaDocument,
    schemaDrafts: drafts.schemaDrafts,
    stagedSuite: lists.stagedSuite,
  });
  // Whether anything in the studio would be lost on leaving — the leave
  // guard (LeaveGuard.tsx, via StudioPipelineProvider) asks on it for
  // scratch work; a draft's own rule is edits since open/save.
  const hasUnsavedWork = hasUnsavedStudioWork({
    pipelineDirty,
    editor,
    schemaDrafts: drafts.schemaDrafts,
  });

  // The hook groups spread whole — their extra internals (setters, load
  // helpers) are harmless surplus on the context object; the overrides after
  // the spreads are the provider-level compositions.
  const value = useMemo(
    () => ({
      ...lists,
      ...editor,
      ...view,
      ...drafts,
      ...scriptDrafts,
      ...previews,
      ...schemaStaging,
      ...selection,
      ...blockOps,
      ...output,
      requestVariantSwitch: variantSwitch.requestVariantSwitch,
      pendingVariantSwitch: variantSwitch.pendingVariantSwitch,
      confirmVariantSwitch: variantSwitch.confirmVariantSwitch,
      cancelVariantSwitch: variantSwitch.cancelVariantSwitch,
      setActiveSaveHandler,
      setDraftSaveHandler,
      requestActiveSave,
      requestDraftSave,
      pendingSeed: variantSwitch.pendingSeed,
      seedingStageIds: variantSwitch.seedingStageIds,
      confirmSeedReplace: variantSwitch.confirmSeedReplace,
      cancelSeedReplace: variantSwitch.cancelSeedReplace,
      variantIsCustom: variantSwitch.variantIsCustom,
      stage: sinkOps.stageResource,
      unstage: sinkOps.unstage,
      renameTransform: sinkOps.renameTransform,
      clearSchemaDraft: drafts.clearDraftFor,
      setStageTab: selectStageTab,
      editorSourceId: editorSource?.id ?? null,
      clearEditorSource,
      editorKind,
      effectiveSelectedId,
      pipelineDirty,
      hasUnsavedWork,
      clearPipeline,
    }),
    [
      lists,
      editor,
      view,
      drafts,
      scriptDrafts,
      previews,
      schemaStaging,
      selection,
      blockOps,
      output,
      variantSwitch,
      setActiveSaveHandler,
      setDraftSaveHandler,
      requestActiveSave,
      requestDraftSave,
      sinkOps,
      selectStageTab,
      editorSource,
      clearEditorSource,
      editorKind,
      effectiveSelectedId,
      pipelineDirty,
      hasUnsavedWork,
      clearPipeline,
    ],
  );

  return (
    <PipelineStagingContext.Provider value={value}>
      {children}
    </PipelineStagingContext.Provider>
  );
}

export function usePipelineStaging(): PipelineStagingValue {
  const ctx = useContext(PipelineStagingContext);
  if (!ctx) {
    throw new Error(
      "usePipelineStaging must be used within PipelineStagingProvider",
    );
  }
  return ctx;
}
